export const CHESSLA_PRODUCT_GUIDE = [
  {
    id: "start",
    title: "One loop. Your own evidence.",
    body: [
      "Import finished games on Analyze. Chessla verifies objective move facts locally and saves only completed evidence.",
      "Review one decision before seeing the answer. Carry the lesson into Patterns, Trends, or a grounded Nikola conversation.",
    ],
  },
  {
    id: "games",
    title: "Choose the right game.",
    body: [
      "Games is your owned library. Format and time-window scope prevent old Bullet decisions from contaminating current Rapid performance.",
      "Open a row for Review. Importing and batch analysis live on Analyze so the library stays focused on selection.",
    ],
  },
  {
    id: "analyze",
    title: "Turn moves into verified facts.",
    body: [
      "Quick, Balanced, and Deep control analysis depth, not coaching confidence. Device capability selects the safest configuration.",
      "Keep the page open until saving finishes. A failed save preserves the game and offers a truthful retry. Downloads remain local to your device.",
    ],
  },
  {
    id: "review",
    title: "Think before Nikola speaks.",
    body: [
      "Guided Review moves through Think, Test, Verify, and Carry. Open Review reveals evidence immediately for experienced players.",
      "Temporary play never changes the recorded game. Nikola supplies a practical imaginary reply and grades objective consequences separately.",
    ],
  },
  {
    id: "principles",
    title: "Name the decision habit.",
    body: [
      "Principles are deterministic decision signals, not generic advice. Applied means the move satisfied the relevant rule without a verified refutation. Missed requires a legal verified alternative and consequence.",
      "Sequence-dependent signals replay exact ordered plies. Broken history, book, forced, sacrificial, ambiguous, or near-threshold positions fail closed. Reanalyze legacy games to generate current Principle evidence.",
    ],
  },
  {
    id: "patterns",
    title: "Require recurrence.",
    body: [
      "Patterns compare verified examples across distinct owned games inside one format and time window. A single occurrence is evidence, not a recurring Pattern.",
      "Focus chooses one bounded behavior to watch. It never turns frequency into strength or private notes into facts.",
      "Pick any pattern, principle, or moment from the list and its saved positions open in one board beside it (full screen on phones), so the page never jumps.",
    ],
  },
  {
    id: "trends",
    title: "Read direction with uncertainty.",
    body: [
      "Charts use owned scoped games. Hover or touch for exact values. Rating, accuracy, errors, clock pressure, activity, and repertoire remain separate KPIs.",
      "Forecasts appear only after sample and calibration gates. Dotted paths are posterior expectations, not invented future games.",
    ],
  },
  {
    id: "nikola",
    title: "Ask through verified context.",
    body: [
      "Choose account, format, game, or exact-move context. Nikola can compose owned analytics and game evidence, run explicitly authorized actions, and link every referenced game.",
      "Nikola explains verified facts. It cannot create analysis truth, inspect games you do not own, mix formats silently, or spend credits without an explicit metered turn.",
    ],
  },
  {
    id: "profile",
    title: "Control your account and data.",
    body: [
      "Profile holds private personal details, password security, your selected public Chess.com source, Nikola context, communication preferences, and deletion controls.",
      "Changing the Chess.com source replaces the previous player dataset so histories never mix. Optional analytics never includes your name, email, games, moves, or Nikola text.",
    ],
  },
] as const;

export type ChesslaGuideTopic = (typeof CHESSLA_PRODUCT_GUIDE)[number]["id"];
export const chesslaGuideTopic = (value: unknown) =>
  typeof value === "string" ? (CHESSLA_PRODUCT_GUIDE.find((section) => section.id === value) ?? null) : null;
